import {CBARObject3D} from "../../components";
import {CBARPipeline} from "./CBARPipeline";
import * as THREE from "three";
import {CBAROpticalFlowNode, OpticalFlowFeature} from "./CBAROpticalFlowNode";
import {Point} from "mirada";
import {denormalizePoint, INT_MAX, normalizePoint} from "../Utils";
import {euclideanDist, euclideanDistSq} from "../Math";
import {CBARFrame} from "../CBARFrame";
import {CBARContext} from "../../CBARContext";

class CBARFeaturePoint {
    constructor(private pipeline:CBARPipeline, referencePoint:Point, public feature?:OpticalFlowFeature) {
        this.adjustedPosition = referencePoint;
    }

    public offset:Point = {x:0, y:0};
    public position3d = new THREE.Vector3();

    private _adjustedPosition!:Point;

    public get adjustedPosition() {
        return this._adjustedPosition;
    }

    public set adjustedPosition(value) {
        if (this.feature) {
            this.offset = {x:this.feature.currentPosition.x - value.x, y:this.feature.currentPosition.y - value.y};
        }
        this._adjustedPosition =  {...value};
        this.position3d = this.pipeline.context.scene!.unproject(normalizePoint(value, this.pipeline.inputSize), 3.0);
    }

    public get isTracked() {
        return !!this.feature?.status
    }

    private get trackedPosition() {
        if (!this.feature) {
            return this._adjustedPosition;
        }
        return {x: this.feature.currentPosition.x - this.offset.x, y: this.feature.currentPosition.y - this.offset.y};
    }

    private get projectedPosition() {
        return denormalizePoint(this.pipeline.context.scene!.project(this.position3d), this.pipeline.inputSize);
    }

    private _lastProjectedPosition:Point|undefined;

    public update() {
        const trackedPosition = this.trackedPosition;

        if (!this.pipeline.context.hasGyroscope) {
            this.adjustedPosition = trackedPosition;
            return
        }

        const projectedPosition = this.projectedPosition;
        let speed = 0;

        if (this._lastProjectedPosition) {
            speed = euclideanDist(this._lastProjectedPosition, projectedPosition);
        }

        this._lastProjectedPosition = projectedPosition;

        if (!this.isTracked || euclideanDist(projectedPosition, trackedPosition) > 20 || speed > 10) {
            this.adjustedPosition = projectedPosition;
            return
        }

        this.adjustedPosition = trackedPosition;
    }
}

export abstract class CBARDynamicObject extends CBARObject3D<CBARDynamicObject> {

    protected constructor(protected pipeline:CBARPipeline, private _seedPoint:Point) {
        super(pipeline.context);
    }

    private _seedFeatures:CBARFeaturePoint[] = [new CBARFeaturePoint(this.pipeline, this._seedPoint)];
    private _setFeaturesTime = 0;

    private _maxFeatures = 7;
    private _featureUpdateMS = 500;

    protected get seedFeatures() {
        this._seedFeatures.forEach(f=>f.update());
        let features = this._seedFeatures.filter(f=>f.isTracked);
        if (features.length < this._maxFeatures && (performance.now() - this._setFeaturesTime) > this._featureUpdateMS) {

            const existingFeatures = features.map(f=>f.feature);
            const newFeatures = this.pipeline.getNode(CBAROpticalFlowNode).features.filter(f=>!existingFeatures.includes(f));
            const numToGrab = Math.min(this._maxFeatures - features.length, newFeatures.length-1);

            if (numToGrab && (numToGrab > 1 || !features.length)) {
                const closest = newFeatures.sort((a,b)=>{
                    const distA = euclideanDistSq(this._seedPoint, a.currentPosition);
                    const distB = euclideanDistSq(this._seedPoint, b.currentPosition);
                    if (distA === distB) return 0;
                    return distA < distB ? -1 : 1;
                });

                const subset = closest.slice(0, numToGrab);
                const featurePoints = subset.map(f=>new CBARFeaturePoint(this.pipeline, this._seedPoint, f));
                this._seedFeatures = features = features.concat(featurePoints);
            }

            this._setFeaturesTime = performance.now();
        }

        return features.length ? features : this._seedFeatures;
    }

    getTransform() {
        //cv.getAffineTransform();
    }

    public get seedPoint() {
        const features = this.seedFeatures;
        const positions = features.map(f=>f.adjustedPosition);
        if (positions.length) {
            //median x and y
            const index = Math.floor(positions.length / 2);
            const xPos = positions.sort(p=>p.x)[index].x;
            const yPos = positions.sort(p=>p.y)[index].y;

            this._seedPoint = {x:xPos, y:yPos};

            //prevent a jump
            features.forEach(f=>f.adjustedPosition = this._seedPoint);
        }

        return this._seedPoint;
    }

    public set seedPoint(point) {
        this._seedPoint = {...point};
        this._seedFeatures = [new CBARFeaturePoint(this.pipeline, this._seedPoint)];//dummy gyroscope only or static point with no gyro
    }

    public update(frame:CBARFrame) {
        //this._seedFeatures.forEach(f=>f.update());

        // if (frame.index % 60 === 0 && this.pipeline.context.scene) {
        //     const screenPoint = {x:this.pipeline.inputSize.width * Math.random(), y:this.pipeline.inputSize.height * Math.random()};
        //     const test = this.pipeline.context.scene.unproject(normalizePoint(screenPoint, this.pipeline.inputSize), 5.0);
        //     const pos = denormalizePoint(this.pipeline.context.scene.project(test), this.pipeline.inputSize);
        //
        //     console.log(`c: ${Math.ceil(screenPoint.x)}, ${Math.ceil(screenPoint.y)} : ${Math.ceil(pos.x)}, ${Math.ceil(pos.y)}`);
        // }

        // const adjusted3d = this.pipeline.context.scene?.pointForward(this.position2d, 3.0);
        // if (adjusted3d) {
        //     this.renderObject.position.set(adjusted3d.x, adjusted3d.y, adjusted3d.z);
        // }
    }
}